import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useLogin } from '@/api/api-hooks/auth-hooks';
import Popup from '@/components/elements/Popup';
import { LabelInput } from '@/components/forms/LabelInput';
import { PasswordInput } from '@/components/forms/PasswordInput';
import AppButton from '@/components/ui/Button';
import useLoginDrawer from '@/hooks/useLoginDrawerStore';

interface Inputs {
  email: string;
  password: string;
}

function LoginForm({ closeForm }: { closeForm: () => void }) {
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Inputs>({
    mode: 'onChange',
  });
  const { openDrawer } = useLoginDrawer();
  const openRecoverPasswordDrawer = () => {
    openDrawer('forgot_pass');
  };
  const { mutate: login, data, isSuccess, isPending } = useLogin();
  const onSubmit: SubmitHandler<Inputs> = (data) => {
    login({
      email: data.email,
      password: data.password,
    });
  };

  useEffect(() => {
    if (data && isSuccess) {
      if (data?.access_token) {
        reset();
        closeForm();
      } else {
        setTitle('Error!');
        setOpenPopup(true);
      }
    }
  }, [closeForm, data, isSuccess, reset]);

  const goToSignUp = () => {
    closeForm();
    openDrawer('register');
  };

  return (
    <>
      <div className="h-full">
        <form onSubmit={handleSubmit(onSubmit)} className="h-full">
          <div className="flex h-[calc(100%-80px)] w-full flex-col justify-between">
            <div className="p-4 md:p-6">
              <LabelInput
                name="email"
                labelText="Email"
                placeholder="Enter your email"
                id="email"
                otherProps={{
                  ...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: 'Email is not correct',
                    },
                    maxLength: { value: 320, message: 'Too long email' },
                  }),
                  ...{ maxLength: 320 },
                }}
                error={errors.email?.message}
              />
              <PasswordInput
                labelText="Password"
                id="password"
                placeholder="Enter your password"
                name="password"
                otherProps={{
                  ...register('password', {
                    required: 'Password is required',
                    maxLength: { value: 40, message: 'Too long password' },
                  }),
                  ...{ maxLength: 40 },
                }}
                error={errors.password?.message}
              />
              <button
                type="button"
                onClick={openRecoverPasswordDrawer}
                className="text-active-primary text-input-label w-fit"
              >
                Forgot Password?
              </button>
              <div className="mt-4 w-full">
                <p className="text-input-label text-second-accent">
                  Don&#39;t you have an account?{' '}
                  <span className="text-active-primary cursor-pointer" onClick={goToSignUp}>
                    Sign up
                  </span>
                </p>
              </div>
            </div>
            <div className="flex w-full items-center justify-end p-5">
              <AppButton onClick={closeForm} variant="secondary" className="mr-4 flex w-fit">
                Cancel
              </AppButton>
              <AppButton disabled={isPending} type="submit" loading={isPending}>
                Log in
              </AppButton>
            </div>
          </div>
        </form>
      </div>
      <Popup isOpen={openPopup} onClose={() => setOpenPopup(false)} title={title}>
        <p>{data?.message}</p>
      </Popup>
    </>
  );
}

export default LoginForm;
